import type { Me } from '@myrewards/contracts';
import { useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState, type ReactElement } from 'react';
import { RouterProvider as AriaRouterProvider } from 'react-aria-components';
import { ScrollRestoration, useHref, useLocation, useMatches, useNavigate } from 'react-router';
import { meQuery, waitingQuery } from '../api';
import { myFeedbackKeys } from '../api/myFeedback';
import { setMotionPreference } from '../design/motion';
import { markVoiceIntroSeen, voiceIntroSeen } from '../feedback/voiceIntro';
import { PreviewOutlet, usePreviewAs } from '../preview';
import { useBell } from '../notifications/useBell';
import { homeHref, presentMe, previewLabel, roleLine, shownRoles } from '../session/presenter';
import { useApplyAccessibility } from '../settings/accessibility';
import { pagePattern, readHeroOverrides, writeHeroOverride } from '../session/heroOverrides';
import { useExitPreview } from '../session/useExitPreview';
import { useSignOut } from '../session/useSignOut';
import {
  AppShell,
  BusyProvider,
  HeroVariantProvider,
  type HeroPreference,
  type LevelOneId,
  type PageHelp,
  type PreviewState,
} from '../shell';
import { ToastProvider } from '../ui';
import { useHelpWiring } from '../help/useHelpWiring';
import { visiblePages, type PageSearch, type SearchPage } from '../search/pages';
import { jumpToFor, recentFor, searchFor } from '../search/present';
import { claimRecent, recentEntryFor, rememberOpen } from '../search/recent';
import { backHere } from './backHere';
import { currentNavItem, type NavItem } from './nav';
import { manageHref } from '../manage/pages';

/** What a route can tell the shell through its `handle`. */
export interface RouteHandle {
  /** Help for the page, fixed or by the person's roles. */
  help?: PageHelp | ((me: Me) => PageHelp);
  /** How the page shows in Search & jump (APP-022); collected from the whole route tree. */
  search?: PageSearch | readonly PageSearch[];
  /**
   * The Level 1 item the page belongs to, marked current in the header (QA-045). Every page
   * names one; a test walks the route tree to keep it so.
   */
  navItem?: NavItem;
  /**
   * The page belongs where it was opened from (the award record): every link to it carries
   * where it was opened from (APP-082).
   */
  opensFrom?: true;
}

/** The signed-in root route's handle: every page the route tree offers to Search & jump. */
export interface RootHandle {
  searchPages: readonly SearchPage[];
  /** The Level 1 item a path belongs to for this person, from the same route tree (QA-045). */
  navOwner: (path: string, me: Me) => LevelOneId | null;
  /** Whether a path's page belongs where it was opened from: the award record (APP-082). */
  opensFrom: (path: string) => boolean;
}

const NO_PAGES: readonly SearchPage[] = [];
const NEVER = (): boolean => false;
const DEFAULT_HELP: PageHelp = { pageLabel: 'MyRewards', topicIds: [] };

function useRootHandle(): RootHandle | undefined {
  return useMatches().find((match) => match.id === 'root')?.handle as RootHandle | undefined;
}

function useSearchPages(): readonly SearchPage[] {
  return useRootHandle()?.searchPages ?? NO_PAGES;
}

/** The Level 1 item the current page belongs to, as the header marks it (QA-045). */
function nowCurrent(
  matches: readonly { handle: unknown; loaderData: unknown }[],
  state: unknown,
  me: Me,
  root: RootHandle | undefined,
): LevelOneId | null {
  const ownerOf = (path: string): LevelOneId | null => root?.navOwner(path, me) ?? null;
  return currentNavItem(matches, { state, me, ownerOf });
}

function useCurrentNavItem(me: Me): LevelOneId | null {
  const matches = useMatches();
  const location = useLocation();
  return nowCurrent(matches, location.state as unknown, me, useRootHandle());
}

function isRouteHandle(value: unknown): value is RouteHandle {
  return typeof value === 'object' && value !== null && 'help' in value;
}

/** The page the header override belongs to: the deepest route, as its pattern (`/awards/:id`). */
function useHeroPage(): string {
  const matches = useMatches();
  const leaf = matches[matches.length - 1];
  return leaf ? pagePattern(leaf.pathname, leaf.params) : '/';
}

/**
 * Recent in Search & jump (SHELL1, APP-085): each award, person or listed page the person opens,
 * however they got there, kept in this browser for them alone. Another person's list goes as
 * soon as someone else is signed in here, and nothing is kept while previewing.
 */
function useRecentOpens(me: Me, pages: readonly SearchPage[]): void {
  const { pathname, search } = useLocation();
  const personId = me.person.id;
  // Acting as a real person in Preview as is their experience, Recent included (APP-122 §10).
  const previewing = me.preview !== null;
  const hrefs = useMemo(
    () => new Set(visiblePages(me, pages).map((page) => page.href)),
    [me, pages],
  );
  useEffect(() => {
    claimRecent(personId);
  }, [personId]);
  useEffect(() => {
    if (previewing) return;
    const entry = recentEntryFor(pathname, search, hrefs);
    if (entry !== null) rememberOpen(personId, entry);
  }, [pathname, search, hrefs, personId, previewing]);
}

/** Help for the deepest matched route that provides it. */
function useRouteHelp(me: Me): PageHelp {
  const matches = useMatches();
  for (let index = matches.length - 1; index >= 0; index -= 1) {
    const handle = matches[index]?.handle;
    if (isRouteHandle(handle) && handle.help) {
      return typeof handle.help === 'function' ? handle.help(me) : handle.help;
    }
  }
  return DEFAULT_HELP;
}

/**
 * The signed-in layout: the app shell around every page. The root loader has already loaded
 * `me` (or redirected to sign-in), so it's in the cache here.
 */
export function RootLayout(): ReactElement {
  const navigate = useNavigate();
  const matches = useMatches();
  const location = useLocation();
  const { data: me } = useQuery(meQuery);
  const root = useRootHandle();
  const opensFrom = root?.opensFrom ?? NEVER;
  return (
    // React Aria links and menu items navigate through React Router (client-side, same origin).
    <AriaRouterProvider
      navigate={(path, options) => {
        // A link to the same page with another query (a filter, a view, a page of a list) keeps
        // the person's place; a link to another page opens at its top (QA-032).
        const target = new URL(path, window.location.href);
        // Every link to the award record says where it was opened from, so its back link returns
        // there and the header marks that page's Level 1 item (QA-045, APP-082). A link that says
        // so itself keeps its own.
        const state =
          options?.state === undefined && me !== undefined && opensFrom(target.pathname)
            ? backHere(
                matches,
                location,
                me,
                nowCurrent(matches, location.state as unknown, me, root),
              )
            : undefined;
        void navigate(path, {
          ...options,
          ...(state !== undefined && { state }),
          preventScrollReset: target.pathname === window.location.pathname,
        });
      }}
      useHref={useHref}
    >
      <BusyProvider>
        <ToastProvider>
          <SignedInShell />
        </ToastProvider>
      </BusyProvider>
      {/* A new page opens at its top and Back returns to where the person was (QA-032); filters
          that only change the query keep their place with `preventScrollReset`. */}
      <ScrollRestoration />
    </AriaRouterProvider>
  );
}

/**
 * The gold banner's words: the role combination being previewed or, acting as a real person
 * (APP-122), who they are ("Graham Whitlock · Sector Lead · Maritime Airborne") and who is really
 * signed in.
 */
function previewBanner(me: Me): PreviewState | null {
  if (me.preview !== null) return { label: previewLabel(me.preview.roles, me.preview.sector) };
  if (me.actingAs == null) return null;
  // APP-232: with the Spot Committee seat as it is today ("Stock Admin · Spot Committee member").
  const line = roleLine(shownRoles(me));
  return {
    label: line === null ? me.person.displayName : `${me.person.displayName} · ${line}`,
    signedInAs: me.actingAs.signedInAs.name,
  };
}

function SignedInShell(): ReactElement {
  const { data: me } = useSuspenseQuery(meQuery);
  const shellMe = useMemo(() => presentMe(me), [me]);
  const help = useRouteHelp(me);
  const { signOut } = useSignOut();
  const queryClient = useQueryClient();
  const feedbackFeatures = me.features?.feedback;
  // APP-104 §3.2a: the voice introduction, once for each person in this browser. It's the
  // signed-in person's, also while they act as someone: the feedback they send is their own.
  const introPerson = me.actingAs?.signedInAs.id ?? me.person.id;
  const [introDoneBy, setIntroDoneBy] = useState<string | null>(null);
  const voiceIntro = useMemo(
    () => introDoneBy !== introPerson && !voiceIntroSeen(introPerson),
    [introDoneBy, introPerson],
  );
  const feedback = useMemo(
    () =>
      feedbackFeatures?.enabled === true
        ? {
            voice: feedbackFeatures.voice,
            dictationTip: feedbackFeatures.dictationTip !== false,
            voiceSecondsLeft: feedbackFeatures.voiceSecondsLeft,
            voiceIntro,
          }
        : null,
    [feedbackFeatures, voiceIntro],
  );
  const exitPreview = useExitPreview();
  const previewAs = usePreviewAs(me);
  // The bell: unread notices, plus the getting-started steps from Waiting on you.
  const { data: waiting } = useQuery(waitingQuery);
  const bell = useBell(waiting);
  const home = homeHref(me);
  // My settings › Accessibility (higher contrast, larger text) on every page.
  useApplyAccessibility();
  // The header opens in the person's default from My settings. Its collapse button overrides
  // that for this page only, in this browser (DESIGN-SPEC §2); it never changes the default.
  const page = useHeroPage();
  const personId = me.person.id;
  const [saved, setSaved] = useState(() => ({
    personId,
    overrides: readHeroOverrides(personId),
  }));
  const heroOverrides = saved.personId === personId ? saved.overrides : readHeroOverrides(personId);
  const heroPreference = useMemo<HeroPreference>(
    () => ({
      variant: heroOverrides[page] ?? me.preferences.hero,
      onChange: (hero) => {
        setSaved({
          personId,
          overrides: writeHeroOverride(personId, page, hero, me.preferences.hero),
        });
      },
    }),
    [heroOverrides, page, personId, me.preferences.hero],
  );
  const searchPages = useSearchPages();
  const current = useCurrentNavItem(me);
  const search = useMemo(() => searchFor(me, searchPages), [me, searchPages]);
  const jumpTo = useMemo(() => jumpToFor(me, searchPages), [me, searchPages]);
  const recent = useMemo(() => recentFor(me, searchPages), [me, searchPages]);
  useRecentOpens(me, searchPages);

  // Keep the document in step if the preference changes while signed in (My settings).
  useEffect(() => {
    setMotionPreference(me.preferences.motion);
  }, [me.preferences.motion]);

  // Help from the server (APP-048): topics, search, the Stock Admin team, and the chat when
  // Admin › AI turned it on for this person. Off: topics only, and nothing goes to a model.
  const helpWiring = useHelpWiring(me, help);

  return (
    <HeroVariantProvider value={heroPreference}>
      <AppShell
        me={shellMe}
        nav={me.nav}
        current={current}
        // APP-234: the header's Manage goes straight to the person's first tab.
        navHrefs={{ ...(home !== '/' && { home }), manage: manageHref(me) }}
        {...(home !== '/' && { homeHref: home })}
        bell={bell}
        canPreview={me.canPreview}
        preview={previewBanner(me)}
        onExitPreview={exitPreview}
        {...(previewAs.open && { onPreviewOpen: previewAs.open })}
        awardsPolicyHref={me.organization.awardsPolicyUrl}
        feedback={feedback}
        onFeedbackOff={() => {
          void queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
        }}
        // The session ended: asking for `me` again meets the 401 the app already handles.
        onFeedbackSessionEnded={() => {
          void queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
        }}
        // A recording ended: the next opening reads the day's voice time afresh.
        onFeedbackVoiceUsed={() => {
          void queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
        }}
        onFeedbackVoiceIntroSeen={() => {
          markVoiceIntroSeen(introPerson);
          setIntroDoneBy(introPerson);
        }}
        // APP-138: Settings › My feedback lists it now, and shows for someone sending their first.
        onFeedbackSent={() => {
          void queryClient.invalidateQueries({ queryKey: meQuery.queryKey });
          void queryClient.invalidateQueries({ queryKey: myFeedbackKeys.all });
        }}
        help={help}
        {...helpWiring}
        search={search}
        searchJumpTo={jumpTo}
        {...(recent && { searchRecent: recent })}
        onSignOut={signOut}
      >
        <PreviewOutlet previewing={me.preview !== null} />
      </AppShell>
      {previewAs.panel}
    </HeroVariantProvider>
  );
}
